import React, { useState, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { Typography } from '../../interfaces/typography';
import { UserDataResult, SettingsUpdate } from '../../interfaces/users';
import { updateUserPriority } from '../../lib/redux/users/slice';
import { getServicesManager } from '../../services';
import { StandardContainer, PrimaryActionButton, SearchInput } from '../../utils/CommonUIComponents';
import { PriorityLevel } from './PriorityLevel';
import { useUserData } from '../../lib/redux/users/user-data-hook';

interface PriorityLevelData {
  level: number;
  users: UserDataResult[];
}

export function PriorityOrderManagement() {
  const dispatch = useDispatch();
  const { allParallelEnabledUsers } = useUserData();
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [pendingChanges, setPendingChanges] = useState<Map<string, number | null>>(new Map());
  const [unassignedSearch, setUnassignedSearch] = useState('');

  // Use parallel enabled users from centralized hook
  const allUsers = allParallelEnabledUsers;

  // Get current priority for user (including pending changes)
  const getCurrentPriority = useCallback((userId: string): number | null => {
    if (pendingChanges.has(userId)) {
      return pendingChanges.get(userId)!;
    }
    const user = allUsers.find(u => u.user_id === userId);
    return user?.inbound_priority || null;
  }, [allUsers, pendingChanges]);

  // Get organized priority levels
  const priorityLevels = React.useMemo(() => {
    const levels = new Map<number, UserDataResult[]>();
    const unassigned: UserDataResult[] = [];

    allUsers.forEach(user => {
      const priority = getCurrentPriority(user.user_id);
      if (priority === null) {
        unassigned.push(user);
      } else {
        if (!levels.has(priority)) {
          levels.set(priority, []);
        }
        levels.get(priority)!.push(user);
      }
    });

    const sortedLevels: PriorityLevelData[] = Array.from(levels.entries())
      .sort(([a], [b]) => a - b)
      .map(([level, users]) => ({ level, users }));

    return { levels: sortedLevels, unassigned };
  }, [allUsers, getCurrentPriority]);

  // Filter unassigned users based on search
  const filteredUnassigned = React.useMemo(() => {
    if (!unassignedSearch.trim()) return priorityLevels.unassigned;
    
    return priorityLevels.unassigned.filter(user => 
      user.user_name.toLowerCase().includes(unassignedSearch.toLowerCase())
    );
  }, [priorityLevels.unassigned, unassignedSearch]);

  // Update user priority
  const updateUserPriorityLocal = (userId: string, newPriority: number | null) => {
    setPendingChanges(prev => new Map(prev).set(userId, newPriority));
    setError(null);
    setSuccessMessage(null);
  };

  // Move user up/down in priority
  const moveUser = (userId: string, direction: 'up' | 'down') => {
    const currentPriority = getCurrentPriority(userId);
    if (currentPriority === null) return;

    const newPriority = direction === 'up' ? currentPriority - 1 : currentPriority + 1;
    if (newPriority < 1) return;

    updateUserPriorityLocal(userId, newPriority);
  };

  // Clear user priority
  const clearUserPriority = (userId: string) => {
    updateUserPriorityLocal(userId, null);
  };

  // Set user to specific priority
  const setUserPriority = (userId: string, priority: number) => {
    if (priority < 1) return;
    updateUserPriorityLocal(userId, priority);
  };

  // Save all changes
  const saveChanges = async () => {
    setIsSaving(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const promises = Array.from(pendingChanges.entries()).map(async ([userId, priority]) => {
        const settingsUpdate: SettingsUpdate = {
          is_team: false,
          targeted_user_id: userId,
          inbound_priority: priority,
        };
        return getServicesManager().postSettings(settingsUpdate);
      });

      await Promise.all(promises);
      
      // Update Redux store immediately with the new priority values
      pendingChanges.forEach((priority, userId) => {
        dispatch(updateUserPriority({ userId, priority }));
      });
      
      setPendingChanges(new Map());
      setSuccessMessage(`Successfully updated priority for ${pendingChanges.size} user(s)`);
    } catch (err) {
      setError('Failed to update priority settings. Please try again.');
      console.error('Error updating priorities:', err);
    } finally {
      setIsSaving(false);
    }
  };

  // Reset changes
  const resetChanges = () => {
    setPendingChanges(new Map());
    setError(null);
    setSuccessMessage(null);
  };

  const hasChanges = pendingChanges.size > 0;

  return (
    <StandardContainer maxWidth="max-w-6xl">
      {/* Header with Actions */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="flex items-center justify-between">
          <div>
            <Typography variant="h2" color="#1F2937" fontWeight={700}>
              Priority Order Management
            </Typography>
            <Typography variant="mediumParagraph" color="#6B7280" className="mt-1">
              Manage inbound priority levels. Lower numbers = higher priority.
            </Typography>
          </div>
          
          {/* Action Buttons */}
          <div className="flex items-center gap-3">
            {hasChanges && (
              <>
                <div className="flex items-center gap-2 text-amber-600 bg-amber-50 px-3 py-1.5 rounded-lg">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.996-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                  <Typography variant="smallParagraph" color="#D97706" fontWeight={500}>
                    {pendingChanges.size} pending changes
                  </Typography>
                </div>
                
                <button
                  onClick={resetChanges}
                  disabled={isSaving}
                  className="px-3 py-1.5 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
                >
                  <Typography variant="smallParagraph" color="#374151" fontWeight={500}>
                    Cancel
                  </Typography>
                </button>
                
                <PrimaryActionButton
                  onClick={saveChanges}
                  disabled={isSaving}
                  icon={
                    isSaving ? (
                      <svg className="w-4 h-4 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                    ) : (
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    )
                  }
                >
                  {isSaving ? 'Saving...' : `Save Changes (${pendingChanges.size})`}
                </PrimaryActionButton>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Status Messages */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <svg className="w-5 h-5 text-red-500 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <Typography variant="mediumParagraph" color="#DC2626" fontWeight={500}>
              {error}
            </Typography>
          </div>
        </div>
      )}

      {successMessage && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center">
            <svg className="w-5 h-5 text-green-500 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <Typography variant="mediumParagraph" color="#059669" fontWeight={500}>
              {successMessage}
            </Typography>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        {/* Priority Levels */}
        <div className="xl:col-span-2">
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-6">
              <Typography variant="h3" color="#1F2937" fontWeight={600}>
                Priority Levels
              </Typography>
              <Typography variant="smallParagraph" color="#6B7280">
                {priorityLevels.levels.reduce((acc, level) => acc + level.users.length, 0)} users assigned
              </Typography>
            </div>

            {priorityLevels.levels.length === 0 ? (
              <div className="text-center py-12 border-2 border-dashed border-gray-300 rounded-lg">
                <Typography variant="mediumParagraph" color="#6B7280">
                  No priority levels set. Assign users from the unassigned list.
                </Typography>
              </div>
            ) : (
              <div className="space-y-4">
                {priorityLevels.levels.map((level, index) => (
                  <PriorityLevel
                    key={level.level}
                    level={level.level}
                    users={level.users}
                    pendingChanges={pendingChanges}
                    onMoveUser={moveUser}
                    onRemoveUser={clearUserPriority}
                    isHighest={level.level === 1}
                    rankLabel={index === 0 ? 'Highest Priority' : index === priorityLevels.levels.length - 1 ? 'Lowest Priority' : 'Medium Priority'}
                    defaultCollapsed={true}
                  />
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Unassigned Users */}
        <div className="xl:col-span-1">
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-6">
              <Typography variant="h3" color="#1F2937" fontWeight={600}>
                Unassigned Users
              </Typography>
              <Typography variant="smallParagraph" color="#6B7280">
                {priorityLevels.unassigned.length} users
              </Typography>
            </div>

            {priorityLevels.unassigned.length === 0 ? (
              <div className="text-center py-8 border-2 border-dashed border-gray-300 rounded-lg">
                <Typography variant="mediumParagraph" color="#6B7280">
                  All users have been assigned priorities.
                </Typography>
              </div>
            ) : (
              <>
                {/* Search for unassigned users */}
                {priorityLevels.unassigned.length > 2 && (
                  <div className="mb-4">
                    <SearchInput
                      value={unassignedSearch}
                      onChange={setUnassignedSearch}
                      placeholder="Search unassigned users..."
                      className="w-full"
                    />
                  </div>
                )}

                <div className="space-y-2">
                  {filteredUnassigned.length === 0 ? (
                    <div className="text-center py-4">
                      <Typography variant="smallParagraph" color="#6B7280">
                        No users match your search
                      </Typography>
                      <button 
                        onClick={() => setUnassignedSearch('')}
                        className="mt-2 text-blue-600 hover:text-blue-800 underline text-sm"
                      >
                        Clear search
                      </button>
                    </div>
                  ) : (
                    filteredUnassigned.map((user) => {
                      const hasChange = pendingChanges.has(user.user_id);
                      return (
                        <div 
                          key={user.user_id} 
                          className={`bg-gray-50 rounded-lg p-3 border ${
                            hasChange ? 'border-amber-300 bg-amber-50' : 'border-gray-200'
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <div className="w-7 h-7 bg-gray-500 rounded-full flex items-center justify-center text-white font-medium text-xs">
                                {user.user_name.charAt(0).toUpperCase()}
                              </div>
                              <div className="flex items-center gap-2">
                                <Typography variant="mediumParagraph" color="#1F2937" fontWeight={500}>
                                  {user.user_name}
                                </Typography>
                                {hasChange && (
                                  <span className="text-xs bg-amber-100 text-amber-800 px-2 py-1 rounded-full">
                                    Modified
                                  </span>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <input
                                type="number"
                                min="1"
                                placeholder="Priority"
                                className="w-20 px-2 py-1 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                onKeyPress={(e) => {
                                  if (e.key === 'Enter') {
                                    const value = parseInt(e.currentTarget.value);
                                    if (value > 0) {
                                      setUserPriority(user.user_id, value);
                                      e.currentTarget.value = '';
                                    }
                                  }
                                }}
                              />
                              <button
                                onClick={(e) => {
                                  const input = e.currentTarget.previousElementSibling as HTMLInputElement;
                                  const value = parseInt(input.value);
                                  if (value > 0) {
                                    setUserPriority(user.user_id, value);
                                    input.value = '';
                                  }
                                }}
                                className="px-3 py-1 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors font-medium text-sm"
                              >
                                <Typography variant="smallParagraph" color="white" fontWeight={500}>
                                  Add
                                </Typography>
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>

                {/* Show search results summary for unassigned */}
                {unassignedSearch && filteredUnassigned.length > 0 && filteredUnassigned.length < priorityLevels.unassigned.length && (
                  <div className="mt-3 pt-3 border-t border-gray-200">
                    <Typography variant="smallParagraph" color="#6B7280">
                      Showing {filteredUnassigned.length} of {priorityLevels.unassigned.length} users. 
                      <button 
                        onClick={() => setUnassignedSearch('')}
                        className="ml-1 text-blue-600 hover:text-blue-800 underline"
                      >
                        Clear search
                      </button>
                    </Typography>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Instructions */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <Typography variant="h4" color="#1F2937" fontWeight={600} className="mb-4">
          Instructions
        </Typography>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="flex items-start gap-2">
            <div className="w-2 h-2 bg-blue-600 rounded-full mt-2 flex-shrink-0"></div>
            <Typography variant="smallParagraph" color="#6B7280">
              Lower numbers = higher priority (Priority 1 is highest)
            </Typography>
          </div>
          <div className="flex items-start gap-2">
            <div className="w-2 h-2 bg-blue-600 rounded-full mt-2 flex-shrink-0"></div>
            <Typography variant="smallParagraph" color="#6B7280">
              Click priority level headers to expand/collapse sections
            </Typography>
          </div>
          <div className="flex items-start gap-2">
            <div className="w-2 h-2 bg-blue-600 rounded-full mt-2 flex-shrink-0"></div>
            <Typography variant="smallParagraph" color="#6B7280">
              Use search to find specific users within large priority levels
            </Typography>
          </div>
          <div className="flex items-start gap-2">
            <div className="w-2 h-2 bg-blue-600 rounded-full mt-2 flex-shrink-0"></div>
            <Typography variant="smallParagraph" color="#6B7280">
              Use arrow buttons to move users between priority levels
            </Typography>
          </div>
          <div className="flex items-start gap-2">
            <div className="w-2 h-2 bg-blue-600 rounded-full mt-2 flex-shrink-0"></div>
            <Typography variant="smallParagraph" color="#6B7280">
              Enter a number and click "Add" to assign unassigned users
            </Typography>
          </div>
          <div className="flex items-start gap-2">
            <div className="w-2 h-2 bg-blue-600 rounded-full mt-2 flex-shrink-0"></div>
            <Typography variant="smallParagraph" color="#6B7280">
              Changes are saved only when you click "Save Changes"
            </Typography>
          </div>
        </div>
      </div>
    </StandardContainer>
  );
}